import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import { Box, Checkbox, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';
import SearchInput from '../../common/components/SearchInput';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import FilterDisplay from './FilterDisplay';

interface CompanyFilterProps {
  attribute: string;
}

export default function CompanyFilter({ attribute }: CompanyFilterProps): React.JSX.Element {
  const { t } = useTranslation(['search']);
  const { items, refine } = useRefinementList({ attribute, limit: 100 });
  const currentRefinements = useNormalizedCurrentRefinements(attribute);
  const [itemQuery, setItemQuery] = useState('');

  const companyOptions = useMemo((): React.JSX.Element[] => (
    items
      .filter(({ label }: { label: string }) => label.toLowerCase().includes(itemQuery.toLowerCase()))
      .map(({ value: company }) => (
        <ListItemButton
          key={company}
          onClick={() => refine(company)}
        >
          <ListItemText primary={company} />
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={currentRefinements.includes(company)}
            />
          </ListItemIcon>
        </ListItemButton>
      ))
  ), [items, itemQuery, currentRefinements]);

  return (
    <FilterDisplay
      label={t('search:suppliers')}
      icon={<PersonOutlineIcon />}
      onClose={() => setItemQuery('')}
      activeItemCount={currentRefinements.length}
      content={(
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <SearchInput
            fullWidth
            value={itemQuery}
            onChange={setItemQuery}
            placeholder={t('search:search_a_particular_supplier')}
          />
          <Typography sx={{ mt: 2, mb: 1 }} variant="titleMedium">{t('search:select_one_or_several_suppliers')}</Typography>
          <List>
            {companyOptions}
          </List>
        </Box>
      )}
    />
  );
}
